import {
  ArrowRightIcon,
  BellRingIcon,
  BikeIcon,
  BotMessageSquareIcon,
  BriefcaseBusinessIcon,
  BusIcon,
  CheckCircle2Icon,
  CircleAlertIcon,
  ClipboardListIcon,
  LanguagesIcon,
  RouteIcon,
  ScanLineIcon,
  ScaleIcon,
  ShieldIcon,
  TruckIcon,
  WorkflowIcon,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useEffect } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { env } from "@/lib/env"

type Capability = {
  description: string
  icon: LucideIcon
  title: string
}

type Segment = {
  detail: string
  icon: LucideIcon
  stressPoint: string
  title: string
}

type RolloutPhase = {
  outcome: string
  title: string
}

const PRIMARY_CTA = "/fleet-safety/pilot"
const SECONDARY_CTA = "/fleet-safety/super-admin"
const CONCEPT_NOTE_DOWNLOAD_URL = "/fleet-pilot-concept-note.md"
const FLEET_LOGO_SRC = "/feedbacknfc-logo.svg"
const FLEET_PAGE_TITLE = "Fleet Safety | Traffic by FeedbackNFC"
const FLEET_PAGE_DESCRIPTION =
  "Fleet Safety by Traffic and FeedbackNFC delivers panic escalation, incident workflows, multilingual intake, and control-room accountability for fleet operations."

const CAPABILITIES: Capability[] = [
  {
    title: "Panic and SOS escalation",
    description:
      "Drivers or staff trigger SOS through SafetyQR tags. Escalation rules route alerts to dispatch, supervisors, and regional control-room leads.",
    icon: BellRingIcon,
  },
  {
    title: "Structured incident capture",
    description:
      "Capture incident type, severity, media evidence, vehicle details, and people involved in one standard operational workflow.",
    icon: ClipboardListIcon,
  },
  {
    title: "Public complaint channel",
    description:
      "Passengers and bystanders can report unsafe behavior or service breaches with location and evidence, without requiring app install.",
    icon: BotMessageSquareIcon,
  },
  {
    title: "Multilingual evidence intake",
    description:
      "Frontline submissions and passenger complaints can be captured in multiple languages to reduce reporting friction across regions.",
    icon: LanguagesIcon,
  },
  {
    title: "Control-room workflows",
    description:
      "Triaging, assignment, escalation acknowledgment, and status transitions happen inside a shared command workflow, not scattered chat threads.",
    icon: WorkflowIcon,
  },
  {
    title: "Claims and FNOL readiness",
    description:
      "Generate first-notice-of-loss aligned incident timelines and evidence packets for insurers, legal teams, and internal audit.",
    icon: ScaleIcon,
  },
]

const SEGMENTS: Segment[] = [
  {
    title: "Long-haul trucking and logistics",
    stressPoint:
      "Night driving, remote corridors, and delayed incident escalation create operational and legal exposure.",
    detail:
      "SafetyQR ensures standardized incident handling from highway events to loading-yard conflicts, with traceable escalation.",
    icon: TruckIcon,
  },
  {
    title: "Intercity bus and staff transport",
    stressPoint:
      "Passenger safety complaints, driver conduct concerns, and emergency response consistency are hard to enforce route-wide.",
    detail:
      "SafetyQR gives control teams one response layer across all routes with complaint intake, duty escalation, and accountability tracking.",
    icon: BusIcon,
  },
  {
    title: "Food delivery and rider fleets",
    stressPoint:
      "High-frequency trips and distributed riders make panic response and evidence quality inconsistent.",
    detail:
      "SafetyQR provides lightweight rider incident capture and supervisor escalation designed for high-volume dispatch operations.",
    icon: BikeIcon,
  },
]

const ROLLOUT_PHASES: RolloutPhase[] = [
  {
    title: "Phase 1: Control perimeter and policy mapping",
    outcome:
      "Define escalation matrix, incident taxonomy, and stakeholder roles. Align fleet operations, HR, legal, and risk owners.",
  },
  {
    title: "Phase 2: Pilot corridor launch",
    outcome:
      "Deploy to one city cluster, route corridor, or rider zone. Benchmark incident reporting latency, closure time, and evidence completeness.",
  },
  {
    title: "Phase 3: Telematics-connected expansion",
    outcome:
      "Roll out fleet-wide while integrating existing GPS/telematics and dispatch stack into SafetyQR response workflows.",
  },
]

const OUTCOMES = [
  "Reduce incident reporting latency from first signal to actionable escalation.",
  "Improve complaint accountability with verifiable response timestamps and owners.",
  "Increase FNOL and insurance-readiness through complete incident evidence chains.",
  "Create executive-level safety visibility across regions, vendors, and operating units.",
]

export function FleetSafetyLandingPage() {
  const hasSchedulingUrl = Boolean(env.fleetPilotSchedulingUrl)

  useEffect(() => {
    const previousTitle = document.title
    const previousDescription = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content")
    const previousOgTitle = document
      .querySelector('meta[property="og:title"]')
      ?.getAttribute("content")
    const previousOgDescription = document
      .querySelector('meta[property="og:description"]')
      ?.getAttribute("content")
    const previousTwitterTitle = document
      .querySelector('meta[name="twitter:title"]')
      ?.getAttribute("content")
    const previousTwitterDescription = document
      .querySelector('meta[name="twitter:description"]')
      ?.getAttribute("content")

    document.title = FLEET_PAGE_TITLE
    setMetaContent('meta[name="description"]', FLEET_PAGE_DESCRIPTION)
    setMetaContent('meta[property="og:title"]', FLEET_PAGE_TITLE)
    setMetaContent('meta[property="og:description"]', FLEET_PAGE_DESCRIPTION)
    setMetaContent('meta[name="twitter:title"]', FLEET_PAGE_TITLE)
    setMetaContent('meta[name="twitter:description"]', FLEET_PAGE_DESCRIPTION)

    return () => {
      document.title = previousTitle
      restoreMetaContent('meta[name="description"]', previousDescription)
      restoreMetaContent('meta[property="og:title"]', previousOgTitle)
      restoreMetaContent('meta[property="og:description"]', previousOgDescription)
      restoreMetaContent('meta[name="twitter:title"]', previousTwitterTitle)
      restoreMetaContent('meta[name="twitter:description"]', previousTwitterDescription)
    }
  }, [])

  return (
    <div className="relative min-h-svh overflow-x-clip">
      <div aria-hidden className="fleet-hero-aura" />

      <header className="sticky top-0 z-40 border-b border-border/45 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center gap-2.5">
            <img src={FLEET_LOGO_SRC} alt="FeedbackNFC" className="h-7 w-auto" />
            <div className="flex items-end gap-2">
              <span className="text-sm font-semibold tracking-wide">Traffic by FeedbackNFC</span>
              <span className="hidden text-xs text-muted-foreground sm:inline">Fleet Safety</span>
            </div>
          </a>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline" className="hidden sm:inline-flex">
              <a href={SECONDARY_CTA}>Open Super Admin Console</a>
            </Button>
            <Button asChild size="sm">
              <a href={PRIMARY_CTA}>Book a Pilot</a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative px-4 pb-18 pt-12 sm:px-6 sm:pb-24 sm:pt-16 lg:px-8 lg:pt-20">
          <div className="mx-auto grid w-full max-w-7xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div className="space-y-7">
              <Badge className="fleet-kicker rounded-full border border-primary/30 bg-primary/12 px-4 py-1.5 text-primary">
                Traffic Platform Product Line
              </Badge>
              <div className="space-y-5">
                <h1 className="font-heading text-4xl leading-[1.08] tracking-tight text-balance sm:text-5xl lg:text-6xl">
                  Telematics tracks the vehicle.
                  <span className="mt-2 block text-primary">SafetyQR runs the response.</span>
                </h1>
                <p className="max-w-2xl text-base leading-8 text-muted-foreground sm:text-lg">
                  Fleet Safety is the incident-response and accountability layer inside
                  Traffic by FeedbackNFC. It closes the gap between GPS visibility and
                  what operations teams must do when panic alerts, complaints, and
                  safety incidents require coordinated action.
                </p>
              </div>
              <div className="grid max-w-3xl gap-3">
                <div className="flex flex-wrap gap-3">
                  <Button asChild size="lg" className="h-12 px-6">
                    <a href={PRIMARY_CTA} className="inline-flex items-center gap-2">
                      Start a Fleet Pilot
                      <ArrowRightIcon className="size-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-12 px-6">
                    <a href="#how-it-works">See How It Works</a>
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild size="lg" variant="outline" className="h-12 px-6">
                    <a download href={CONCEPT_NOTE_DOWNLOAD_URL}>
                      Download Pilot Brief
                    </a>
                  </Button>
                  {hasSchedulingUrl ? (
                    <Button asChild size="lg" variant="outline" className="h-12 px-6">
                      <a href={env.fleetPilotSchedulingUrl} rel="noreferrer" target="_blank">
                        Schedule Demo
                      </a>
                    </Button>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border/80 bg-background/70 px-4 py-3 text-sm text-muted-foreground">
                      Demo scheduling link appears here once configured.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Card className="fleet-reveal shell-panel rounded-4xl border-border/70 lg:-mb-8">
              <CardHeader className="space-y-5 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <Badge variant="secondary" className="rounded-full px-3 py-1">
                    Control-Room View
                  </Badge>
                  <span className="rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold tracking-[0.16em] text-primary">
                    LIVE RESPONSE MODEL
                  </span>
                </div>
                <CardTitle className="font-heading text-2xl leading-tight">
                  Safety command layer for distributed fleets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  "SOS alert enters command queue with route, vehicle, and staff context.",
                  "Supervisor escalation follows preconfigured timelines and severity rules.",
                  "Evidence and response actions are locked to an auditable incident timeline.",
                ].map((item, index) => (
                  <div
                    key={item}
                    className="fleet-reveal flex items-start gap-3 rounded-2xl border border-border/60 bg-card/65 px-4 py-3"
                    style={{ animationDelay: `${120 + index * 90}ms` }}
                  >
                    <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-sm leading-6 text-foreground/90">{item}</p>
                  </div>
                ))}
                <div className="grid gap-3 pt-2 sm:grid-cols-3">
                  {[
                    { label: "Signal to owner", value: "< 2 min" },
                    { label: "Evidence chain", value: "Structured" },
                    { label: "Accountability", value: "Audit-ready" },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-2xl border border-border/55 bg-background/80 px-3 py-3 text-center"
                    >
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{stat.label}</p>
                      <p className="mt-1 font-heading text-xl text-primary">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="px-4 pb-8 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl rounded-4xl border border-amber-300/55 bg-[linear-gradient(120deg,oklch(0.97_0.022_87.9/.92),oklch(0.95_0.031_77.4/.8))] p-6 shadow-[0_18px_48px_oklch(0.52_0.04_76.9/.15)] sm:p-8">
            <p className="fleet-kicker text-xs font-semibold text-amber-900/85">Positioning Clarity</p>
            <h2 className="mt-2 font-heading text-2xl leading-tight text-amber-950 sm:text-3xl">
              Fleet safety response is different from site safety and personal safety.
            </h2>
            <p className="mt-3 max-w-4xl text-sm leading-7 text-amber-950/85 sm:text-base">
              This surface is purpose-built for moving vehicles, route operations, and
              transport control rooms. It is not a repackaged site-perimeter safety
              workflow. It handles panic escalation, transit incidents, passenger and
              public complaints, and post-incident accountability at fleet scale.
            </p>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
          <div className="mx-auto w-full max-w-7xl">
            <div className="grid gap-5 lg:grid-cols-2">
              <Card className="rounded-4xl border-rose-300/45 bg-rose-50/65">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3 font-heading text-2xl text-rose-950">
                    <RouteIcon className="size-5 text-rose-700" />
                    What telematics already does
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-rose-950/90">
                  <p>Location history, route path, speed, stop duration, and utilization visibility.</p>
                  <p>Useful for operations efficiency, route optimization, and dispatch analysis.</p>
                </CardContent>
              </Card>

              <Card className="rounded-4xl border-cyan-300/50 bg-cyan-50/65">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3 font-heading text-2xl text-cyan-950">
                    <CircleAlertIcon className="size-5 text-cyan-700" />
                    What telematics does not solve
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-cyan-950/90">
                  <p>
                    Panic/SOS workflow ownership, complaint intake, response SLAs, and evidence
                    chain standardization across fleet teams.
                  </p>
                  <p>
                    SafetyQR adds this response operating layer while integrating with existing
                    telematics investments.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8" id="capabilities">
          <div className="mx-auto w-full max-w-7xl">
            <div className="mb-10 space-y-4">
              <p className="fleet-kicker text-xs font-semibold text-primary">Core Capabilities</p>
              <h2 className="font-heading text-3xl leading-tight sm:text-5xl">
                Built for response discipline, not just monitoring
              </h2>
              <p className="max-w-3xl text-base leading-7 text-muted-foreground sm:text-lg">
                Fleet Safety combines panic channels, incident workflows, and audit trails so
                operations leaders can execute consistent response across every route and vendor.
              </p>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {CAPABILITIES.map((capability, index) => {
                const Icon = capability.icon
                return (
                  <Card
                    key={capability.title}
                    className="fleet-reveal shell-panel rounded-4xl border-border/70"
                    style={{ animationDelay: `${80 + index * 70}ms` }}
                  >
                    <CardHeader className="space-y-3 pb-3">
                      <div className="w-fit rounded-2xl border border-primary/30 bg-primary/12 p-2.5">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <CardTitle className="font-heading text-xl leading-snug">{capability.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-7 text-muted-foreground">{capability.description}</p>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8" id="segments">
          <div className="mx-auto w-full max-w-7xl">
            <div className="mb-10 space-y-4">
              <p className="fleet-kicker text-xs font-semibold text-primary">Use Cases by Fleet Type</p>
              <h2 className="font-heading text-3xl leading-tight sm:text-5xl">
                One operating model, adapted per fleet segment
              </h2>
            </div>
            <div className="grid gap-5 lg:grid-cols-3">
              {SEGMENTS.map((segment, index) => {
                const Icon = segment.icon
                return (
                  <Card
                    key={segment.title}
                    className="fleet-reveal rounded-4xl border-border/70 bg-card/80"
                    style={{ animationDelay: `${110 + index * 90}ms` }}
                  >
                    <CardHeader className="space-y-3 pb-3">
                      <div className="w-fit rounded-2xl border border-border/60 bg-background/75 p-2.5">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <CardTitle className="font-heading text-xl leading-snug">{segment.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm leading-7 text-muted-foreground">
                      <p>{segment.stressPoint}</p>
                      <div className="rounded-2xl border border-primary/20 bg-primary/8 p-3 text-foreground/85">
                        {segment.detail}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl">
            <div className="mb-10 space-y-4">
              <p className="fleet-kicker text-xs font-semibold text-primary">How It Works</p>
              <h2 className="font-heading text-3xl leading-tight sm:text-5xl">
                Safety response flow from field signal to closure
              </h2>
            </div>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  title: "Signal capture",
                  description:
                    "SOS trigger or public complaint enters the queue with vehicle, route, and duty context.",
                  icon: ScanLineIcon,
                },
                {
                  title: "Command triage",
                  description:
                    "Control-room teams classify severity, assign owner, and activate escalation ladders by rule.",
                  icon: ShieldIcon,
                },
                {
                  title: "Response execution",
                  description:
                    "Dispatch and supervision actions are tracked with timestamps, status changes, and exception handling.",
                  icon: WorkflowIcon,
                },
                {
                  title: "Evidence and closure",
                  description:
                    "Incident timeline, media, notes, and outcomes are retained for claims, audits, and governance.",
                  icon: ScaleIcon,
                },
              ].map((step, index) => {
                const Icon = step.icon
                return (
                  <div
                    key={step.title}
                    className="fleet-reveal relative rounded-4xl border border-border/70 bg-card/72 p-6"
                    style={{ animationDelay: `${100 + index * 80}ms` }}
                  >
                    <span className="fleet-kicker text-xs font-semibold text-primary">Step {index + 1}</span>
                    <div className="mt-4 w-fit rounded-2xl border border-primary/25 bg-primary/10 p-2.5">
                      <Icon className="size-5 text-primary" />
                    </div>
                    <h3 className="mt-4 font-heading text-xl leading-tight">{step.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-muted-foreground">{step.description}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8" id="rollout">
          <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[1fr_1fr]">
            <Card className="rounded-4xl border-border/70 bg-card/72">
              <CardHeader className="space-y-3 pb-3">
                <p className="fleet-kicker text-xs font-semibold text-primary">Rollout Model</p>
                <CardTitle className="font-heading text-3xl leading-tight">
                  Phased deployment with existing telematics in place
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {ROLLOUT_PHASES.map((phase) => (
                  <div key={phase.title} className="rounded-2xl border border-border/65 bg-background/70 p-4">
                    <h3 className="text-sm font-semibold text-foreground">{phase.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{phase.outcome}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="shell-panel rounded-4xl border-border/70">
              <CardHeader className="space-y-3 pb-3">
                <p className="fleet-kicker text-xs font-semibold text-primary">Target Buyers</p>
                <CardTitle className="font-heading text-3xl leading-tight">
                  Stakeholders who own safety accountability
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  {
                    icon: BriefcaseBusinessIcon,
                    text: "Fleet operations heads and transport command-center leaders",
                  },
                  {
                    icon: ShieldIcon,
                    text: "Security, compliance, and risk governance teams",
                  },
                  {
                    icon: RouteIcon,
                    text: "Dispatch managers responsible for route-level incident closure",
                  },
                  {
                    icon: ScaleIcon,
                    text: "Claims, legal, and insurance liaison stakeholders",
                  },
                ].map((buyer) => (
                  <div key={buyer.text} className="flex items-start gap-3 rounded-2xl border border-border/55 bg-card/60 px-4 py-3">
                    <buyer.icon className="mt-0.5 size-4 shrink-0 text-primary" />
                    <p className="text-sm leading-6 text-foreground/90">{buyer.text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="px-4 pb-20 pt-6 sm:px-6 lg:px-8 lg:pb-24">
          <div className="mx-auto w-full max-w-7xl rounded-4xl border border-border/70 bg-[linear-gradient(140deg,oklch(0.26_0.024_202.4/.96),oklch(0.31_0.034_194.5/.94))] p-8 text-slate-50 shadow-[0_24px_70px_oklch(0.19_0.03_209.2/.45)] sm:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
              <div className="space-y-5">
                <p className="fleet-kicker text-xs font-semibold text-cyan-100/90">Business Outcomes</p>
                <h2 className="font-heading text-3xl leading-tight text-balance sm:text-5xl">
                  Convert safety intent into measurable operating outcomes.
                </h2>
                <p className="max-w-2xl text-base leading-8 text-slate-100/85">
                  Fleet Safety by Traffic and FeedbackNFC helps teams execute repeatable
                  incident response with traceability from first alert to final closure.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button asChild size="lg" className="h-12 bg-cyan-300 px-6 font-semibold text-cyan-950 hover:bg-cyan-200">
                    <a href={PRIMARY_CTA}>Plan a 30-day pilot</a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="h-12 border-cyan-100/45 bg-transparent px-6 text-cyan-50 hover:bg-cyan-200/10 hover:text-cyan-50"
                  >
                    <a href={SECONDARY_CTA}>Open platform console</a>
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {OUTCOMES.map((outcome) => (
                  <div key={outcome} className="flex gap-3 rounded-2xl border border-cyan-100/30 bg-cyan-100/8 px-4 py-3">
                    <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-cyan-200" />
                    <p className="text-sm leading-6 text-slate-100">{outcome}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

function setMetaContent(selector: string, content: string) {
  const metaElement = document.querySelector<HTMLMetaElement>(selector)
  if (metaElement) {
    metaElement.setAttribute("content", content)
  }
}

function restoreMetaContent(selector: string, content: string | null | undefined) {
  const metaElement = document.querySelector<HTMLMetaElement>(selector)
  if (!metaElement || content === undefined || content === null) {
    return
  }

  metaElement.setAttribute("content", content)
}
